import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Banknote, LogOut, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { bottomNavFor, menusFor } from "@/lib/menus";
import { ROLE_LABELS } from "@/lib/format";
import { BrandMark, ThemeToggle } from "@/components/common";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { initResortFilter, usePoll } from "@/lib/live";
import { PetugasGate } from "@/components/PetugasGate";
import { LockScreen, useIdleLock } from "@/components/LockScreen";
import { loadBranch, useBranch } from "@/lib/branch";
import { applyTerms } from "@/lib/terms";

const Badge = ({ n, className = "" }) => (n > 0 ? <span className={cn("min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold grid place-items-center leading-none", className)} data-testid="pinjaman-pending-badge">{n > 99 ? "99+" : n}</span> : null);

const NavItem = ({ m, onClick }) => (
  <NavLink
    to={m.path}
    onClick={onClick}
    data-testid={`nav-${m.key}`}
    className={({ isActive }) => cn(
      "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px]",
      isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground",
    )}
  >
    <m.icon className="h-[18px] w-[18px] shrink-0" />
    <span className="truncate">{m.label}</span>
    <Badge n={m.badge} className="ml-auto" />
  </NavLink>
);

const NavList = ({ menus, onClick }) => {
  let lastGroup = null;
  return (
    <nav className="space-y-1">
      {menus.map((m) => {
        const header = m.group && m.group !== lastGroup ? m.group : null;
        lastGroup = m.group || null;
        return (
          <div key={m.key}>
            {header && <p className="eyebrow px-3 pt-4 pb-2">{header}</p>}
            <NavItem m={m} onClick={onClick} />
          </div>
        );
      })}
    </nav>
  );
};

const LogoutButton = ({ onLogout }) => (
  <button
    onClick={onLogout}
    data-testid="logout-button"
    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 min-h-[44px] transition-colors"
  >
    <LogOut className="h-[18px] w-[18px]" /> Keluar
  </button>
);

const MenuTile = ({ m, onClick }) => (
  <NavLink
    to={m.path}
    onClick={onClick}
    data-testid={`more-nav-${m.key}`}
    className={({ isActive }) => cn(
      "flex flex-col items-start gap-3 rounded-2xl border p-4 transition-colors min-h-[92px]",
      isActive ? "border-primary bg-primary/10 text-primary" : "bg-card hover:bg-muted text-foreground",
    )}
  >
    <span className={cn("h-10 w-10 rounded-xl grid place-items-center shrink-0 bg-primary/12 text-primary")}>
      <m.icon className="h-5 w-5" />
    </span>
    <span className="text-sm font-semibold leading-tight flex items-center gap-2">{m.label}<Badge n={m.badge} /></span>
  </NavLink>
);

const BottomLink = ({ m }) => (
  <NavLink to={m.path} data-testid={`bottom-nav-${m.key}`}
    className={({ isActive }) => cn("relative flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium min-h-[60px] transition-colors", isActive ? "text-primary" : "text-muted-foreground")}>
    <m.icon className="h-5 w-5" />
    <Badge n={m.badge} className="absolute top-1 left-1/2 ml-1" />
    <span className="truncate max-w-[70px]">{m.short || m.label.split(" ")[0]}</span>
  </NavLink>
);

const CenterLink = ({ m }) => (
  <NavLink to={m.path} data-testid="bottom-nav-pinjaman-center" className="flex flex-col items-center justify-end pb-2 min-h-[60px]">
    {({ isActive }) => (
      <>
        <span className={cn("relative -mt-7 h-14 w-14 rounded-full grid place-items-center shadow-lg ring-4 ring-background transition-transform active:scale-95",
          isActive ? "bg-primary text-primary-foreground scale-105" : "bg-primary text-primary-foreground")}>
          <Banknote className="h-6 w-6" />
          <Badge n={m.badge} className="absolute -top-1 -right-1 ring-2 ring-background" />
        </span>
        <span className={cn("text-[11px] font-semibold mt-1", isActive ? "text-primary" : "text-muted-foreground")}>{m.label}</span>
      </>
    )}
  </NavLink>
);

const MoreButton = ({ onClick }) => (
  <button onClick={onClick} data-testid="bottom-nav-more" className="flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium text-muted-foreground min-h-[60px]">
    <Menu className="h-5 w-5" /> Lainnya
  </button>
);

const UserChip = ({ user }) => (
  <div className="rounded-xl bg-muted/60 p-3">
    <p className="text-sm font-semibold truncate" data-testid="current-user-name">{user.name}</p>
    <p className="text-xs text-muted-foreground truncate" data-testid="current-user-role">{ROLE_LABELS[user.role]}</p>
    {(user.cabang_name || user.wilayah_name) && (
      <p className="text-xs text-primary font-medium truncate mt-0.5">{user.cabang_name || user.wilayah_name}{user.resort_name ? ` · Resort ${user.resort_name}` : ""}</p>
    )}
  </div>
);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const branch = useBranch();
  useEffect(() => { if (user.cabang_id) loadBranch(); }, [user.cabang_id]);
  useEffect(() => {
    if (user.wilayah_id || user.role === "viewer") api.get("/terms").then((r) => applyTerms(r.data.pairs)).catch(() => {});
    return () => applyTerms([]);
  }, [user.wilayah_id, user.role]);
  const [pending, setPending] = useState(0);
  const canApprove = ["petugas", "kepala_bagian"].includes(user.role);
  const loadPending = () => { if (canApprove) api.get("/loans/pending-count").then((r) => setPending(r.data.count)).catch(() => {}); };
  useEffect(loadPending, [canApprove]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(loadPending);
  const menus = menusFor(user.role, settings.hidden_menus, branch.menu_order).map((m) => (m.key === "pinjaman" ? { ...m, badge: pending } : m));
  const { center, side, more } = bottomNavFor(user.role, menus);
  if (user.role === "petugas") initResortFilter(user.id); else initResortFilter(null);
  useEffect(() => {
    if (!navigator.geolocation) return undefined;
    const id = navigator.geolocation.watchPosition((p) => { window.__gps = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }; }, () => {}, { enableHighAccuracy: true, maximumAge: 10000 });
    const send = () => { if (window.__gps && window.__checkedIn && !document.hidden) api.post("/tracking", window.__gps).catch(() => {}); };
    const t = user.role === "petugas" && user.active ? setInterval(send, 15000) : null;
    if (t) setTimeout(send, 3000);
    return () => { navigator.geolocation.clearWatch(id); if (t) clearInterval(t); };
  }, [user.role, user.active]);
  const doLogout = () => { logout(); navigate("/login", { replace: true }); };
  const [locked, unlock] = useIdleLock(user);

  return (
    <div className="min-h-screen bg-background">
      {locked && <LockScreen onUnlock={unlock} onLogout={doLogout} />}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 flex-col border-r bg-card/70 backdrop-blur-xl p-5 z-30">
        <div className="flex items-center gap-3 px-1 mb-8">
          <BrandMark />
          <div className="min-w-0">
            <p className="font-heading font-bold leading-tight truncate" data-testid="sidebar-app-name">{settings.app_name}</p>
            <p className="text-xs text-muted-foreground">{ROLE_LABELS[user.role]}</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto -mx-1 px-1"><NavList menus={menus} /></div>
        <div className="space-y-3 pt-4 border-t">
          <UserChip user={user} />
          <LogoutButton onLogout={doLogout} />
        </div>
      </aside>

      <div className="lg:pl-72">
        <header className="sticky top-0 z-20 h-16 border-b bg-background/75 backdrop-blur-xl flex items-center gap-3 px-4 sm:px-6 lg:px-10">
          <div className="lg:hidden flex items-center gap-2.5 min-w-0">
            <BrandMark size="h-9 w-9" />
            <p className="font-heading font-bold truncate">{settings.app_name}</p>
          </div>
          <div className="hidden lg:block text-sm text-muted-foreground">
            {user.cabang_name ? `Kantor Cabang ${user.cabang_name}` : user.wilayah_name ? `Kantor Wilayah ${user.wilayah_name}` : "Kantor Pusat"}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
          </div>
        </header>
        <main className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 pb-28 lg:pb-12 max-w-[1400px]">
          <PetugasGate onLogout={doLogout}><Outlet /></PetugasGate>
        </main>
      </div>

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t bg-card/90 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]" data-testid="bottom-nav">
        <div className="grid grid-cols-5 items-end">
          {center ? (
            <>
              {side[0] ? <BottomLink m={side[0]} /> : <span />}
              {side[1] ? <BottomLink m={side[1]} /> : <span />}
              <CenterLink m={center} />
              {side[2] ? <BottomLink m={side[2]} /> : <span />}
            </>
          ) : side.map((m) => <BottomLink key={m.key} m={m} />)}
          <MoreButton onClick={() => setOpen(true)} />
        </div>
      </nav>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="lg:hidden rounded-t-3xl border-t p-0 pb-[env(safe-area-inset-bottom)] max-h-[85vh] flex flex-col" data-testid="more-menu-sheet">
          <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted-foreground/30" aria-hidden />
          <SheetHeader className="px-6 pt-4 pb-2">
            <SheetTitle className="font-heading">Menu Lainnya</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-4">
            {more.length > 0 ? (
              <div className="grid grid-cols-2 gap-3" data-testid="more-menu-grid">
                {more.map((m) => <MenuTile key={m.key} m={m} onClick={() => setOpen(false)} />)}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="more-menu-empty">Semua menu sudah ada di navigasi bawah.</p>
            )}
            <div className="mt-5 space-y-3 pt-4 border-t">
              <UserChip user={user} />
              <LogoutButton onLogout={doLogout} />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
